import PropTypes from 'prop-types';
import { find, propEq, path, filter } from 'ramda';
import React, { Component } from 'react';
import { Grid, Row, Col } from 'react-flexbox-grid';

import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';

import { connect } from 'react-redux';
import { readEndpoint, createResource, updateResource, deleteResource } from 'redux-json-api';
import { SubmissionError } from 'redux-form';

import { closeLeftBarModule, showSnackbarMessage } from '../../modules/actions/layout';
import ApiUserForm from './Form';

const styles = {
  column: {
    margin: 20,
    width: '100%',
  },
};

class ApiUserEdit extends Component {
  static propTypes = {
    apiEndpoint: PropTypes.string.isRequired,
    readEndpoint: PropTypes.func.isRequired,
    createResource: PropTypes.func.isRequired,
    updateResource: PropTypes.func.isRequired,
    deleteResource: PropTypes.func.isRequired,
    closeLeftBarModule: PropTypes.func.isRequired,
    showSnackbarMessage: PropTypes.func.isRequired,
    extra: PropTypes.shape({
      id: PropTypes.string,
    }),
    apiUser: PropTypes.shape({}),
    apiRoles: PropTypes.array,
  };

  constructor(props) {
    super(props);

    this.state = {
      loading: true,
    };
  };

  componentDidMount() {
    const { readEndpoint, extra: { id } } = this.props;

    readEndpoint(`api-user/${id}`).then(() => {
      readEndpoint(`api-role?filter[user__exact]=${id}`).finally(() => {
        this.setState({ loading: false });
      });
    });
  }

  render() {
    const { loading } = this.state;

    if (loading) {
      return (
        <Grid fluid>
          <Row>
            <Col>
              Loading...
            </Col>
          </Row>
        </Grid>
      );
    }

    const {
      apiUser, apiRoles,
      apiEndpoint, createResource, updateResource, deleteResource,
      closeLeftBarModule, showSnackbarMessage,
    } = this.props;

    const roles = apiRoles.map(role => {
      return {
        id: role.id,
        role: role.attributes.role,
        company: role.relationships.company.data.id,
      };
    });
    const initialValues = {
      email: path(['attributes', 'email'], apiUser) || 'Onbekend',
      first_name: path(['attributes', 'first-name'], apiUser) || 'Onbekend',
      last_name: path(['attributes', 'last-name'], apiUser) || 'Onbekend',
      phone: path(['attributes', 'phone'], apiUser) || 'Onbekend',
      default_company: path(['relationships', 'default-company', 'data', 'id'], apiUser) || '',
      is_active: path(['attributes', 'is-active'], apiUser),
      roles: roles,
      phone_24h_2: path(['attributes', 'phone-24h-2'], apiUser) || '',
      text_notification: path(['attributes', 'text-notification'], apiUser),
    };

    return (
      <Grid fluid>
        <Row>
          <Col style={styles.column}>
            <CardHeader title="Gebruiker wijzigen" />
            <Divider />

            <ApiUserForm
              initialValues={initialValues}
              onSubmit={async (values) => {
                /**
                 * First, try updating the user.
                 */
                try {
                  const apiUserResource = {
                    type: 'api-user',
                    id: apiUser.id,
                    attributes: {
                      'email': values.email,
                      'first-name': values.first_name,
                      'last-name': values.last_name,
                      'phone': values.phone,
                      'phone_24h_2': values.phone_24h_2,
                      'text-notification': values.text_notification,
                    },
                  };

                  /**
                   * If a password has been set, update it.
                   */
                  if (values.password) apiUserResource.attributes['password'] = values.password;

                  /**
                   * If the default-company has changed, update it.
                   */
                  if (values.default_company &&
                      values.default_company !== path(['relationships', 'default-company', 'data', 'id'], apiUser))
                    apiUserResource.attributes['default-company'] = `${apiEndpoint}/company/${values.default_company}`;

                  // If the user's active state has changed, update it.
                  if (typeof values.is_active === 'boolean' &&
                      values.is_active !== path(['attributes', 'is-active'], apiUser))
                    apiUserResource.attributes['is-active'] = values.is_active;

                  await updateResource(apiUserResource);
                } catch (exc) {
                  exc.response.data && exc.response.data.errors.forEach(e => {
                    const field = path(['source', 'pointer'], e)
                      .replace('/data/attributes/', '')
                      .replace('-', '_');

                    if (field === 'non_field_errors') errors._error = e.detail;
                    else {
                      errors[field] = e.detail;

                      switch (field) {
                        case 'email': {
                          errors[field] += ' (voorbeeld: h.devries@arrivatouring.nl)';
                          break;
                        }
                        case 'first_name': {
                          errors[field] += ' (voorbeeld: Henk)';
                          break;
                        }
                        case 'last_name': {
                          errors[field] += ' (voorbeeld: de Vries)';
                          break;
                        }
                        case 'phone': {
                          errors[field] += ' (voorbeeld: 0505260268)';
                          break;
                        }
                      }
                    }
                  });

                  showSnackbarMessage('Er is iets fout gegaan bij het wijzigen van de gebruiker.');

                  throw new SubmissionError(errors);
                }

                /**
                 * Then, try to update the role(s).
                 */
                const roleErrors = [];

                /**
                 * Delete all ApiRoles no longer present.
                 */
                for (let i = 0; i < roles.length; i++) {
                  const role = roles[i];

                  if (!find(propEq('id', role.id), values.roles)) {
                    try {
                      await deleteResource({
                        type: 'api-role',
                        id: role.id,
                      });
                    } catch (exc) {
                      exc.response.data && exc.response.data.errors.forEach(e => {
                        if (e.role) roleErrors.push(e.role);
                        else if (e.user) roleErrors.push(e.user);
                        else if (e.detail) roleErrors.push(e.detail);
                        else roleErrors.push(JSON.stringify(e));
                      });
                    }
                  }
                }

                /**
                 * Create any new ApiRoles.
                 */
                if (values.roles) {
                  for (let i = 0; i < values.roles.length; i++) {
                    const role = values.roles[i];

                    if (Number(role.id) === -1) {
                      try {
                        await createResource({
                          type: 'api-role',
                          attributes: {
                            'role': role.role,
                            'user': `${apiEndpoint}/api-user/${apiUser.id}`,
                            'company': `${apiEndpoint}/company/${role.company}`,
                          },
                        });
                      } catch (exc) {
                        exc.response.data && exc.response.data.errors.forEach(e => {
                          if (e.role) roleErrors.push(e.role);
                          else if (e.user) roleErrors.push(e.user);
                          else if (e.detail) roleErrors.push(e.detail);
                          else roleErrors.push(JSON.stringify(e));
                        });
                      }
                    }
                  }
                }

                if (roleErrors.length > 0) {
                  showSnackbarMessage('Er is iets fout gegaan bij het wijzigen van de rollen.');
                  throw new SubmissionError({ roles: roleErrors.join('\n') });
                }

                showSnackbarMessage('Gebruiker gewijzigd');
                closeLeftBarModule('apiUser');
              }}
              handleDelete={async () => {
                try {
                  const apiUserResource = {
                    type: 'api-user',
                    id: apiUser.id,
                    attributes: { deleted_at: new Date() },
                  }

                  await updateResource(apiUserResource);
                  closeLeftBarModule('apiUser');
                  showSnackbarMessage('Gebruiker verwijderd');
                } catch (exc) {
                  showSnackbarMessage('Er is iets misgegaan bij het verwijderen van de gebruiker.');

                  throw new SubmissionError(exc);
                }
              }}
            />
          </Col>
        </Row>
      </Grid>
    );
  };
};

function selectApiUser(state, id) {
  const apiUsers = state.api['api-user'] && state.api['api-user'].data || [];

  return find(propEq('id', id))(apiUsers);
}

function selectApiRoles(state, userId) {
  const apiRoles = state.api['api-role'] && state.api['api-role'].data || [];
  const isRoleForUser = role => path(['relationships', 'user', 'data', 'id'], role) === userId;

  return filter(isRoleForUser, apiRoles);
}

const mapStateToProps = (state, ownProps) => ({
  apiEndpoint: path(['api', 'endpoint', 'axiosConfig', 'baseURL'], state),
  apiUser: selectApiUser(state, ownProps.extra.id),
  apiRoles: selectApiRoles(state, ownProps.extra.id),
});

const mapDispatchToProps = {
  readEndpoint,
  createResource,
  updateResource,
  deleteResource,
  closeLeftBarModule,
  showSnackbarMessage,
};

export default connect(mapStateToProps, mapDispatchToProps)(ApiUserEdit);
